const CACHE_KEY = 'stand_ig_availability_v1';
const TTL_AVAILABLE_MS = 7 * 24 * 60 * 60 * 1000;
const TTL_UNAVAILABLE_MS = 6 * 60 * 60 * 1000;
const PROBE_CONCURRENCY = 2;
const PROBE_TIMEOUT_MS = 10000;
const MIN_IMAGE_WIDTH = 80;

const INACTIVE_VALUES = new Set(['0', 'false', 'no', 'nao', 'não', 'off', 'inativo']);

export const getInstagramThumbUrl = (shortcode) =>
    `https://www.instagram.com/p/${shortcode}/media/?size=l`;

const readCache = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const writeCache = (cache) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {
        // Storage full or disabled — checks still work, just without persistence.
    }
};

const getCachedStatus = (shortcode) => {
    const entry = readCache()[shortcode];
    if (!entry) return null;

    const ttl = entry.available ? TTL_AVAILABLE_MS : TTL_UNAVAILABLE_MS;
    if (Date.now() - entry.checkedAt > ttl) return null;

    return entry.available;
};

export const markInstagramPostAvailable = (shortcode) => {
    if (!shortcode) return;
    const cache = readCache();
    cache[shortcode] = { available: true, checkedAt: Date.now() };
    writeCache(cache);
};

export const markInstagramPostUnavailable = (shortcode) => {
    if (!shortcode) return;
    const cache = readCache();
    cache[shortcode] = { available: false, checkedAt: Date.now() };
    writeCache(cache);
};

const probeInstagramThumbnail = (shortcode) =>
    new Promise((resolve) => {
        const img = new Image();
        let settled = false;

        const finish = (available) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            img.onload = null;
            img.onerror = null;
            img.src = '';
            resolve(available);
        };

        const timer = setTimeout(() => finish(false), PROBE_TIMEOUT_MS);

        img.onload = () => {
            finish(img.naturalWidth >= MIN_IMAGE_WIDTH);
        };
        img.onerror = () => finish(false);

        img.referrerPolicy = 'no-referrer';
        img.src = getInstagramThumbUrl(shortcode);
    });

const runWithConcurrency = async (items, concurrency, worker) => {
    const results = new Array(items.length);
    let index = 0;

    const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (index < items.length) {
            const current = index;
            index += 1;
            results[current] = await worker(items[current], current);
        }
    });

    await Promise.all(runners);
    return results;
};

const isSheetActive = (car) => {
    if (car.ativo === undefined || car.ativo === null || car.ativo === '') return true;
    return !INACTIVE_VALUES.has(String(car.ativo).trim().toLowerCase());
};

const checkShortcode = async (shortcode) => {
    const cached = getCachedStatus(shortcode);
    if (cached !== null) return cached;

    const available = await probeInstagramThumbnail(shortcode);
    if (available) markInstagramPostAvailable(shortcode);
    else markInstagramPostUnavailable(shortcode);
    return available;
};

/**
 * Filters cars whose Instagram post is unavailable or marked inactive in the sheet.
 * Probes are cached and throttled to avoid hammering Instagram.
 */
export const filterAvailableInstagramCars = async (cars = []) => {
    const sheetFiltered = cars.filter(isSheetActive);

    const withShortcode = sheetFiltered.filter((car) => car.instagramShortcode);
    const withoutShortcode = sheetFiltered.filter((car) => !car.instagramShortcode);

    if (withShortcode.length === 0) {
        return sheetFiltered;
    }

    const availability = await runWithConcurrency(
        withShortcode,
        PROBE_CONCURRENCY,
        async (car) => {
            const available = await checkShortcode(car.instagramShortcode);
            return { car, available };
        }
    );

    const availableCars = availability
        .filter(({ available }) => available)
        .map(({ car }) => car);

    return [...withoutShortcode, ...availableCars];
};
